import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CreditCard, Smartphone, DollarSign, Banknote, Copy, ExternalLink, Loader2 } from "lucide-react";
import { formatConfirmation } from "@/utils/confirmation";

const phoneDigits = (p) => (p || "").replace(/[^0-9]/g, "");

const cashtagFor = (driver) => {
  const t = (driver?.cashapp_tag || "").trim();
  if (!t) return "";
  return t.startsWith("$") ? t : `$${t}`;
};

const copy = (text, label = "Copied") => {
  try {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  } catch {
    toast.error("Copy failed — long-press to copy manually");
  }
};

export default function DriverPaymentModal({ open, onClose, booking, driver, API, token, onPaid }) {
  const balance = Math.max(
    0,
    ((booking?.trip_price || 0) + (booking?.luggage_fee || 0)) - (booking?.amount_paid || 0)
  );
  const [amount, setAmount] = useState(balance.toFixed(2));
  const [cashLoading, setCashLoading] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeUrl, setStripeUrl] = useState("");

  // Reset state every time the modal opens for a different booking
  useEffect(() => {
    if (open && booking) {
      setAmount(balance.toFixed(2));
      setStripeUrl("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, booking?.id]);

  if (!booking) return null;

  const cashtag = cashtagFor(driver);
  const zelleHandle = (driver?.zelle_email || "").trim() || `+1${phoneDigits(driver?.phone).slice(-10)}`;
  const zellePhone = `+1${phoneDigits(driver?.phone).slice(-10)}`;

  const cashappUrl = cashtag
    ? `https://cash.app/${cashtag.replace(/^\$/, "$")}/${amount || ""}`
    : "";
  const zelleNote = `Easy Transport ${formatConfirmation(booking.id)} — ${booking.full_name}`;

  const handleCashPaid = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    setCashLoading(true);
    try {
      const res = await axios.put(
        `${API}/driver/bookings/${booking.id}/cash-payment`,
        { amount: amt },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        res.data.fully_paid
          ? `Cash payment of $${res.data.amount_recorded} recorded — fully paid ✓`
          : `Recorded $${res.data.amount_recorded}. Balance remaining: $${res.data.balance_due}`
      );
      onPaid?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to record cash payment");
    } finally {
      setCashLoading(false);
    }
  };

  const handleStripeLink = async () => {
    setStripeLoading(true);
    try {
      const res = await axios.post(
        `${API}/driver/bookings/${booking.id}/payment-link`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStripeUrl(res.data.checkout_url);
      toast.success("Stripe checkout link ready — share it with the passenger");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create payment link");
    } finally {
      setStripeLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="driver-payment-modal">
        <DialogHeader>
          <DialogTitle>Take Payment — {booking.full_name}</DialogTitle>
          <DialogDescription>
            Balance due: <span className="font-bold text-green-700">${balance.toFixed(2)}</span>
            {booking.amount_paid > 0 && (
              <span className="text-xs text-muted-foreground ml-2">
                (already paid ${booking.amount_paid})
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="mb-3">
          <Label htmlFor="payment-amount" className="text-xs">Amount to collect ($)</Label>
          <Input
            id="payment-amount"
            type="number"
            step="0.01"
            min="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            data-testid="payment-amount-input"
            className="h-10"
          />
        </div>

        <Tabs defaultValue="cash" className="w-full">
          <TabsList className="grid grid-cols-4">
            <TabsTrigger value="cash" data-testid="tab-cash">
              <Banknote className="w-4 h-4 mr-1" /> Cash
            </TabsTrigger>
            <TabsTrigger value="zelle" data-testid="tab-zelle">
              <DollarSign className="w-4 h-4 mr-1" /> Zelle
            </TabsTrigger>
            <TabsTrigger value="cashapp" data-testid="tab-cashapp" disabled={!cashtag}>
              <Smartphone className="w-4 h-4 mr-1" /> CashApp
            </TabsTrigger>
            <TabsTrigger value="stripe" data-testid="tab-stripe">
              <CreditCard className="w-4 h-4 mr-1" /> Card
            </TabsTrigger>
          </TabsList>

          {/* CASH TAB */}
          <TabsContent value="cash" className="mt-4 space-y-3">
            <p className="text-sm text-gray-600">
              Confirm that the passenger handed you <span className="font-bold">${amount || "0"}</span> in cash.
            </p>
            <Button
              className="w-full bg-green-600 hover:bg-green-700 text-white"
              onClick={handleCashPaid}
              disabled={cashLoading}
              data-testid="cash-confirm-btn"
            >
              {cashLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Banknote className="w-4 h-4 mr-2" />}
              Confirm Cash Received
            </Button>
            <p className="text-[10px] text-center text-amber-700">
              This updates the booking immediately — only confirm after you have the cash in hand.
            </p>
          </TabsContent>

          {/* ZELLE TAB */}
          <TabsContent value="zelle" className="mt-4 space-y-3 text-center">
            <p className="text-sm text-gray-600">Passenger scans this QR to send via Zelle.</p>
            <div className="flex justify-center bg-white p-3 rounded-lg border-2 border-purple-200">
              <QRCodeSVG value={zelleHandle} size={180} level="M" />
            </div>
            <div className="text-sm space-y-1">
              <p className="font-medium text-gray-700">Zelle: <span className="font-bold text-purple-700">{zelleHandle}</span></p>
              <p className="text-xs text-gray-500">Name: {driver?.name || "Driver"}</p>
              <p className="text-xs text-gray-500">Memo: {zelleNote}</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" size="sm" onClick={() => copy(zelleHandle, "Zelle handle")}>
                <Copy className="w-3 h-3 mr-1" /> Copy Handle
              </Button>
              <Button variant="outline" size="sm" onClick={() => copy(`$${amount} — ${zelleNote}`, "Memo")}>
                <Copy className="w-3 h-3 mr-1" /> Copy Memo
              </Button>
            </div>
            <Button
              className="w-full bg-purple-600 hover:bg-purple-700 text-white"
              onClick={handleCashPaid}
              disabled={cashLoading}
              data-testid="zelle-received-btn"
            >
              {cashLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              I received the Zelle — mark paid
            </Button>
          </TabsContent>

          {/* CASHAPP TAB */}
          <TabsContent value="cashapp" className="mt-4 space-y-3 text-center">
            {!cashtag ? (
              <p className="text-sm text-gray-500">No CashApp tag on file. Ask the admin to add it in your driver profile.</p>
            ) : (
              <>
                <p className="text-sm text-gray-600">Passenger scans to pay <span className="font-bold">${amount || "0"}</span> directly.</p>
                <div className="flex justify-center bg-white p-3 rounded-lg border-2 border-green-200">
                  <QRCodeSVG value={cashappUrl} size={180} level="M" />
                </div>
                <div className="text-sm">
                  <p className="font-medium text-gray-700">CashApp: <span className="font-bold text-green-700">{cashtag}</span></p>
                  <p className="text-xs text-gray-500">Tap link to send: {cashappUrl}</p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" size="sm" onClick={() => copy(cashtag, "Cashtag")}>
                    <Copy className="w-3 h-3 mr-1" /> Copy Cashtag
                  </Button>
                  <a href={cashappUrl} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="sm" className="w-full">
                      <ExternalLink className="w-3 h-3 mr-1" /> Open CashApp
                    </Button>
                  </a>
                </div>
                <Button
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                  onClick={handleCashPaid}
                  disabled={cashLoading}
                  data-testid="cashapp-received-btn"
                >
                  {cashLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  I received the CashApp — mark paid
                </Button>
              </>
            )}
          </TabsContent>

          {/* STRIPE TAB */}
          <TabsContent value="stripe" className="mt-4 space-y-3 text-center">
            <p className="text-sm text-gray-600">Generate a card payment link and share it with the passenger.</p>
            {!stripeUrl ? (
              <Button
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
                onClick={handleStripeLink}
                disabled={stripeLoading}
                data-testid="stripe-generate-btn"
              >
                {stripeLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
                Create Stripe Checkout Link
              </Button>
            ) : (
              <>
                <div className="flex justify-center bg-white p-3 rounded-lg border-2 border-indigo-200">
                  <QRCodeSVG value={stripeUrl} size={180} level="M" />
                </div>
                <p className="text-xs text-gray-500 break-all">{stripeUrl}</p>
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" size="sm" onClick={() => copy(stripeUrl, "Link")}>
                    <Copy className="w-3 h-3 mr-1" /> Copy Link
                  </Button>
                  <a
                    href={`https://wa.me/${phoneDigits(booking.whatsapp_number)}?text=${encodeURIComponent(`Hi ${booking.full_name?.split(" ")[0] || "there"}, here's the secure card payment link for your Easy Transportation trip: ${stripeUrl}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button variant="outline" size="sm" className="w-full">
                      <ExternalLink className="w-3 h-3 mr-1" /> Send via WhatsApp
                    </Button>
                  </a>
                </div>
                <p className="text-[10px] text-amber-700">
                  Stripe will auto-mark the booking as paid once the passenger completes checkout.
                </p>
              </>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
